// ═══════════════════════════════════════════════════════════════════
//  modules/projets/ProjectView.js
//  ProjectView — les trois colonnes tout de suite
//
//  ── PROJET_DIRECT (06/09/2026) ──────────────────────────────────────
//  Avant, ouvrir un projet ne montrait AUCUNE tâche : il fallait
//  d'abord choisir une catégorie. Une porte de plus devant l'information
//  qu'on venait chercher — et le seul moyen de voir « tout ce qui reste
//  à faire » était d'ouvrir les dix catégories l'une après l'autre.
//
//  Maintenant : les trois colonnes s'affichent d'emblée, toutes
//  catégories confondues. La catégorie n'est plus une porte, c'est une
//  pastille sur la carte et un filtre au-dessus.
//
//  Un projet est désormais un JALON — « Bêta fermée », « Bêta ouverte » —
//  et pas un thème. La catégorie dit QUOI, le projet dit QUAND.
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { Badge } from "../../composants/Badge";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";
import { CATEGORY_LABELS } from "./constantes";
import { NewTaskModal } from "./NewTaskModal";
import { ProjectChat } from "./ProjectChat";
import { TaskCard } from "./TaskCard";

const COLONNES = [
  { cle: "a_faire", titre: "À faire", couleur: THEME.gold },
  { cle: "en_cours", titre: "En cours", couleur: THEME.accent },
  { cle: "termine", titre: "Terminé", couleur: THEME.emerald },
];

const ETATS = {
  a_faire: { label: "À faire", bg: THEME.goldDim, color: THEME.gold },
  en_cours: { label: "En cours", bg: THEME.accentDim, color: THEME.accent },
  termine: { label: "Terminé", bg: THEME.emeraldDim, color: THEME.emerald },
  archive: { label: "Archivé", bg: THEME.surface, color: THEME.textMuted },
};

// Même repli que dans ProjectsList : si le back ne renvoie pas encore
// `etat`, on le recalcule plutôt que d'afficher un badge vide.
function etatDe(p) {
  if (!p) return "a_faire";
  if (p.etat) return p.etat;
  if (p.status === "cloture") return "archive";
  if (!p.total_tasks) return "a_faire";
  if (p.done_tasks === p.total_tasks) return "termine";
  return p.done_tasks > 0 ? "en_cours" : "a_faire";
}

export function ProjectView({ token, projectId, currentUserId, onBack }) {
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [filtre, setFiltre] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const loadProject = useCallback(async () => {
    const res = await fetch(`${API_BASE}/api/projects`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    setProject(Array.isArray(data) ? data.find((p) => p.id === projectId) : null);
  }, [projectId, token]);

  const loadTasks = useCallback(async () => {
    const res = await fetch(`${API_BASE}/api/projects/${projectId}/tasks`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    setTasks(Array.isArray(data) ? data : []);
  }, [projectId, token]);

  useEffect(() => {
    loadProject(); loadTasks();
    const i = setInterval(() => { loadProject(); loadTasks(); }, 6000);
    return () => clearInterval(i);
  }, [loadProject, loadTasks]);

  if (!project) return <div style={{ color: THEME.textMuted }}>Chargement...</div>;

  // Le filtre ne propose que les catégories PRÉSENTES dans ce projet :
  // offrir « Lore » sur un jalon qui n'en contient aucune, c'est offrir
  // un bouton qui vide l'écran.
  const presentes = [...new Set(tasks.map((t) => t.category))].filter(Boolean);
  const visibles = filtre ? tasks.filter((t) => t.category === filtre) : tasks;
  const e = ETATS[etatDe(project)] || ETATS.a_faire;

  const chip = (actif, couleur, fond) => ({
    background: actif ? fond : "transparent",
    border: `1px solid ${actif ? couleur : THEME.border}`,
    color: actif ? couleur : THEME.textMuted,
    borderRadius: 999, padding: "5px 13px", cursor: "pointer", fontSize: 12,
    fontWeight: actif ? 700 : 500,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button onClick={onBack} style={{ background: "transparent", border: `1px solid ${THEME.border}`, color: THEME.textMuted, borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 13 }}>← Tous les projets</button>
        <h2 style={{ color: THEME.gold, fontSize: 20, fontWeight: 800, margin: 0 }}>{project.name}</h2>
        <Badge text={e.label} bg={e.bg} color={e.color} />
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowModal(true)} style={{ background: THEME.accentDim, border: `1px solid ${THEME.accent}`, color: THEME.accent, borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>+ Nouvelle tâche</button>
      </div>

      {project.description && (
        <div style={{ color: THEME.textMuted, fontSize: 13, marginTop: -8 }}>{project.description}</div>
      )}

      <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 10, padding: "16px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ color: THEME.textMuted, fontSize: 12, fontWeight: 600 }}>PROGRESSION GLOBALE</span>
          <span style={{ color: THEME.gold, fontSize: 13, fontWeight: 700 }}>{project.progress}% ({project.done_tasks}/{project.total_tasks} tâches)</span>
        </div>
        <div style={{ background: THEME.border, borderRadius: 6, height: 10, overflow: "hidden" }}>
          <div style={{ background: THEME.gold, width: `${project.progress}%`, height: "100%", transition: "width 0.4s ease" }} />
        </div>
      </div>

      {presentes.length > 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <span style={{ color: THEME.textMuted, fontSize: 11, fontWeight: 600, textTransform: "uppercase", marginRight: 4 }}>Filtrer</span>
          <button onClick={() => setFiltre(null)} style={chip(!filtre, THEME.gold, THEME.goldDim)}>
            Tout · {tasks.length}
          </button>
          {presentes.map((k) => {
            const v = CATEGORY_LABELS[k];
            const n = tasks.filter((t) => t.category === k).length;
            return (
              <button key={k} onClick={() => setFiltre(filtre === k ? null : k)}
                style={chip(filtre === k, v ? v.color : THEME.textMuted, v ? v.bg : THEME.surface)}>
                {(v ? v.label : k)} · {n}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14, alignItems: "start" }}>
        {COLONNES.map((c) => {
          const dedans = visibles.filter((t) => t.status === c.cle);
          return (
            <div key={c.cle} style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 10, padding: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, paddingBottom: 10, borderBottom: `1px solid ${THEME.border}` }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: c.couleur }} />
                <span style={{ color: c.couleur, fontWeight: 700, fontSize: 14 }}>{c.titre}</span>
                <span style={{ color: THEME.textMuted, fontSize: 12 }}>{dedans.length}</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {dedans.map((t) => <TaskCard key={t.id} task={t} token={token} onUpdated={loadTasks} />)}
                {dedans.length === 0 && <div style={{ color: THEME.textDim, fontSize: 12, textAlign: "center", padding: 12 }}>Aucune tâche</div>}
              </div>
            </div>
          );
        })}
      </div>

      <ProjectChat token={token} projectId={projectId} currentUserId={currentUserId} />

      {showModal && <NewTaskModal token={token} projectId={projectId} defaultCategory={filtre || "site_admin"} onClose={() => setShowModal(false)} onCreated={() => { setShowModal(false); loadTasks(); }} />}
    </div>
  );
}
